import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartOptions
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { StageAverageDurationChartDataType } from "@/components/layouts/StageAverageDuration/StageAverageDuration";
import { useTranslation } from "react-i18next";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import { map } from "lodash";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  annotationPlugin,
  ChartDataLabels
);

interface Props {
  chartData: StageAverageDurationChartDataType[];
  selectedStageIndex: number;
}

type annotationsType = {
  [key: string]: {
    type: string;
    xMin: number;
    xMax: number;
    borderColor: string;
    borderWidth: number;
    borderDash: number[];
    drawTime: string;
  };
};

const stepSizeForMaxSevenSpaces = (num: number) => {
  const stepSize = num / 7;
  const roundedStepSize = Math.ceil(stepSize);
  return roundedStepSize;
};

const getColors = (
  chartData: StageAverageDurationChartDataType[],
  colorType: "background" | "border"
) => {
  return map(chartData, (element) => {
    return getColorForMilestoneByType(element?.milestone, colorType);
  });
};

const MINIMAL_DURATION = 0.05;
const MINIMAL_WEEKS_TO_DISPLAY = 4;

const StageAverageDurationChart: React.FC<Props> = ({
  chartData,
  selectedStageIndex
}) => {
  const { t } = useTranslation();
  const data = {
    labels: map(chartData, (prop) => prop.label),
    datasets: [
      {
        label: "Start",
        data: map(chartData, (prop) => prop.start),
        backgroundColor: "transparent"
      },
      {
        label: "Duration",
        data: map(chartData, (prop) => prop.duration + MINIMAL_DURATION),
        backgroundColor: getColors(chartData, "background"),
        borderColor: getColors(chartData, "border"),
        borderWidth: (context: any) => {
          const index = context.dataIndex;
          const selected = index === selectedStageIndex;
          return {
            top: 5,
            right: selected ? 2 : 0,
            bottom: selected ? 2 : 0,
            left: selected ? 2 : 0
          };
        },
        hoverBackgroundColor: getColors(chartData, "background"),
        hoverBorderColor: getColors(chartData, "border"),
        borderRadius: 5,
        borderSkipped: false
      }
    ]
  };

  const getStepSizeAndMaxValue = () => {
    const maxValue = Math.max(
      data.datasets[0].data[data.datasets[0].data.length - 1] +
        data.datasets[1].data[data.datasets[1].data.length - 1],
      MINIMAL_WEEKS_TO_DISPLAY
    );
    const stepSize = stepSizeForMaxSevenSpaces(maxValue);

    return { stepSize, maxValue };
  };

  const getAnnotations = () => {
    const annotationConstants = {
      type: "line",
      borderWidth: 1,
      borderDash: [5, 5],
      borderColor: "#475467",
      drawTime: "beforeDatasetsDraw"
    };
    const annotations: annotationsType = {};

    const { stepSize, maxValue } = getStepSizeAndMaxValue();

    for (let index = 0; index < maxValue + 1; index += stepSize) {
      if (index === 0 || index % stepSize === 0) {
        annotations[`line${index + 1}`] = {
          ...annotationConstants,
          xMin: index,
          xMax: index
        };
      }
    }
    return annotations;
  };

  const options: ChartOptions<"bar"> = {
    maintainAspectRatio: false,
    responsive: true,
    indexAxis: "y",
    layout: {
      padding: {
        left: 22
      }
    },
    scales: {
      x: {
        border: {
          color: "transparent"
        },
        grid: {
          display: false
        },
        position: "top",
        stacked: true,
        ticks: {
          callback: function (value) {
            const { stepSize } = getStepSizeAndMaxValue();
            if (typeof value === "number" && value % stepSize === 0) {
              return value;
            }
            return "";
          },
          stepSize: getStepSizeAndMaxValue().stepSize,
          font: {
            size: 12,
            family: "Open Sans",
            weight: "bold"
          },
          color: "#98A2B3"
        }
      },
      y: {
        display: false,
        stacked: true
      }
    },
    onHover: (event: any, elements: any[]) => {
      if (event.native) {
        event.native.target.style.cursor =
          elements.length > 0 ? "pointer" : "default";
      }
    },
    plugins: {
      tooltip: {
        enabled: false
      },
      legend: {
        display: false
      },
      annotation: {
        annotations: getAnnotations() as any
      },
      datalabels: {
        anchor: "end",
        align: "start",
        color: "#E4E7EC",
        font: {
          size: 14,
          family: "Open Sans"
        },
        formatter: function (value, context) {
          const datasetIndex = context.datasetIndex;
          if (datasetIndex === 1) {
            return Math.floor(value) + t("stageAverageDuration.weeksUnit");
          }
          return null;
        }
      }
    }
  };

  return (
    <div className="w-full h-full">
      <Bar data={data} options={options} />
    </div>
  );
};

export default StageAverageDurationChart;
